import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  BookOpen,
  Layers,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Trash2,
  X,
} from 'lucide-react';
import AppLoader from '../../components/modules/AppLoader';
import ModuleAlert from '../../components/modules/ModuleAlert';
import ModulePageHeader from '../../components/modules/ModulePageHeader';
import { getThemeColor } from '../../config/appConfig';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../utils/apiService';
import '../../styles/module-shared.css';
import './education.css';
import {
  COURSE_STATUSES,
  emptyModuleForm,
  formatCourseDate,
  isRichTextHtml,
  normalizeCourse,
  normalizeModule,
  validateModuleForm,
} from './courseUtils';

const THEME = getThemeColor('primary');

const CourseDetails = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [error, setError] = useState('');
  const [modulesError, setModulesError] = useState('');
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [moduleModalOpen, setModuleModalOpen] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [moduleForm, setModuleForm] = useState(emptyModuleForm(1));
  const [moduleFormError, setModuleFormError] = useState('');
  const [moduleSaving, setModuleSaving] = useState(false);

  const loadModules = useCallback(async () => {
    setModulesLoading(true);
    setModulesError('');
    try {
      const res = await apiService.getEducationModulesList(courseId);
      if (!res.success) {
        setModules([]);
        setModulesError(res.error || res.message || 'Failed to load modules');
        return;
      }
      const payload = res.data ?? res.result ?? [];
      const list = Array.isArray(payload) ? payload : [];
      setModules(list.map(normalizeModule).filter(Boolean));
    } catch (err) {
      setModules([]);
      setModulesError(err.message || 'Failed to load modules');
    } finally {
      setModulesLoading(false);
    }
  }, [courseId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiService.getEducationCourseById(courseId);
      if (!res.success) {
        setCourse(null);
        setError(res.error || res.message || 'Failed to load course');
        return;
      }
      setCourse(normalizeCourse(res.data ?? res.result));
      await loadModules();
    } catch (err) {
      setCourse(null);
      setError(err.message || 'Failed to load course');
    } finally {
      setLoading(false);
    }
  }, [courseId, loadModules]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreateModule = () => {
    const nextOrder = (modules.reduce((max, m) => Math.max(max, m.displayOrder || 0), 0) || 0) + 1;
    setEditingModule(null);
    setModuleForm(emptyModuleForm(nextOrder));
    setModuleFormError('');
    setModuleModalOpen(true);
  };

  const openEditModule = (mod) => {
    setEditingModule(mod);
    setModuleForm({
      title: mod.title || '',
      description: mod.description || '',
      displayOrder: String(mod.displayOrder || 1),
      status: mod.isActive ? 'Active' : 'Inactive',
    });
    setModuleFormError('');
    setModuleModalOpen(true);
  };

  const closeModuleModal = () => {
    if (moduleSaving) return;
    setModuleModalOpen(false);
    setEditingModule(null);
    setModuleFormError('');
  };

  const saveModule = async (e) => {
    e.preventDefault();
    const validation = validateModuleForm(moduleForm);
    if (validation) {
      setModuleFormError(validation);
      return;
    }

    setModuleSaving(true);
    setModuleFormError('');
    try {
      const payload = {
        title: moduleForm.title.trim(),
        description: moduleForm.description?.trim() || null,
        displayOrder: Number(moduleForm.displayOrder),
        status: moduleForm.status === 'Inactive' ? 'Inactive' : 'Active',
      };
      const res = editingModule
        ? await apiService.updateEducationModule({
            courseModuleId: editingModule.id,
            ...payload,
          })
        : await apiService.createEducationModule({
            courseId: Number(courseId),
            ...payload,
          });

      if (!res.success) {
        setModuleFormError(res.error || res.message || 'Save failed');
        return;
      }

      showSuccess(res.message || (editingModule ? 'Module updated' : 'Module created'));
      setModuleModalOpen(false);
      setEditingModule(null);
      await loadModules();
    } catch (err) {
      setModuleFormError(err.message || 'Save failed');
    } finally {
      setModuleSaving(false);
    }
  };

  const runConfirm = async () => {
    if (!confirmAction) return;
    setActionBusy(true);
    try {
      let res;
      const { type, target } = confirmAction;

      if (type === 'activate') res = await apiService.activateEducationCourse(course.id);
      else if (type === 'inactivate') res = await apiService.inactivateEducationCourse(course.id);
      else if (type === 'delete') res = await apiService.deleteEducationCourse(course.id);
      else if (type === 'module-activate') {
        res = await apiService.activateEducationModule(target.id);
      } else if (type === 'module-inactivate') {
        res = await apiService.inactivateEducationModule(target.id);
      } else if (type === 'module-delete') {
        res = await apiService.deleteEducationModule(target.id);
      } else return;

      if (!res.success) {
        showError(res.error || res.message || 'Action failed');
        return;
      }
      showSuccess(res.message || 'Updated successfully');
      setConfirmAction(null);
      if (type === 'delete') {
        navigate('/education/courses');
        return;
      }
      if (type.startsWith('module-')) {
        await loadModules();
        return;
      }
      await load();
    } catch (err) {
      showError(err.message || 'Action failed');
    } finally {
      setActionBusy(false);
    }
  };

  const moveModule = async (mod, direction) => {
    setActionBusy(true);
    try {
      const res = await apiService.moveEducationModule(mod.id, direction);
      if (!res.success) {
        showError(res.error || res.message || 'Failed to move module');
        return;
      }
      const payload = res.data ?? res.result;
      if (Array.isArray(payload)) {
        setModules(payload.map(normalizeModule).filter(Boolean));
      } else {
        await loadModules();
      }
      showSuccess(res.message || 'Module order updated');
    } catch (err) {
      showError(err.message || 'Failed to move module');
    } finally {
      setActionBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="page edu-page" style={{ '--edu-primary': THEME }}>
        <div className="page-inner">
          <div className="module-card">
            <AppLoader color={THEME} />
          </div>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="page edu-page" style={{ '--edu-primary': THEME }}>
        <div className="page-inner">
          <button type="button" className="edu-back" onClick={() => navigate('/education/courses')}>
            <ArrowLeft className="w-4 h-4" />
            Back to courses
          </button>
          <ModuleAlert message={error || 'Course not found'} />
        </div>
      </div>
    );
  }

  const confirmCopy =
    confirmAction?.type === 'delete'
      ? 'Are you sure you want to delete this course?'
      : confirmAction?.type === 'inactivate'
        ? 'Are you sure you want to make this course inactive?'
        : confirmAction?.type === 'activate'
          ? 'Are you sure you want to activate this course?'
          : confirmAction?.type === 'module-delete'
            ? 'Are you sure you want to delete this module?'
            : confirmAction?.type === 'module-inactivate'
              ? 'Are you sure you want to make this module inactive?'
              : confirmAction?.type === 'module-activate'
                ? 'Are you sure you want to activate this module?'
                : '';

  const confirmName =
    confirmAction?.type?.startsWith('module-')
      ? confirmAction.target?.title
      : course.title;

  return (
    <div className="page edu-page" style={{ '--edu-primary': THEME }}>
      <div className="page-inner">
        <button type="button" className="edu-back" onClick={() => navigate('/education/courses')}>
          <ArrowLeft className="w-4 h-4" />
          Back to courses
        </button>

        <div className="edu-header-row">
          <ModulePageHeader
            icon={BookOpen}
            title={course.title}
            subtitle={course.shortDescription || 'Course details'}
            iconBg={`linear-gradient(135deg,${THEME},#a52a2a)`}
          />
          <div className="edu-actions-row" style={{ marginTop: 0 }}>
            <button
              type="button"
              className="edu-btn edu-btn--ghost"
              onClick={() => navigate(`/education/courses/${course.id}/edit`)}
            >
              <Pencil className="w-4 h-4" />
              Edit Course
            </button>
            {course.isActive ? (
              <button
                type="button"
                className="edu-btn edu-btn--ghost"
                onClick={() => setConfirmAction({ type: 'inactivate' })}
              >
                <PowerOff className="w-4 h-4" />
                Inactivate
              </button>
            ) : (
              <button
                type="button"
                className="edu-btn edu-btn--primary"
                onClick={() => setConfirmAction({ type: 'activate' })}
              >
                <Power className="w-4 h-4" />
                Activate
              </button>
            )}
            <button
              type="button"
              className="edu-btn edu-btn--danger"
              onClick={() => setConfirmAction({ type: 'delete' })}
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </div>

        <ModuleAlert message={error} />

        <div className="edu-detail-layout">
          <section className="module-card edu-section">
            {course.thumbnailUrl ? (
              <img className="edu-thumb" src={course.thumbnailUrl} alt="" />
            ) : (
              <div className="edu-thumb edu-thumb-placeholder">
                <BookOpen className="w-10 h-10" />
              </div>
            )}
            <dl className="edu-meta edu-meta-grid">
              <div>
                <dt>Category</dt>
                <dd>{course.category || '—'}</dd>
              </div>
              <div>
                <dt>Level</dt>
                <dd>{course.level || '—'}</dd>
              </div>
              <div>
                <dt>Duration</dt>
                <dd>{course.duration || '—'}</dd>
              </div>
              <div>
                <dt>Language</dt>
                <dd>{course.language || '—'}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>
                  <span className={`edu-status ${course.isActive ? 'is-active' : 'is-inactive'}`}>
                    {course.status}
                  </span>
                </dd>
              </div>
              <div>
                <dt>Created</dt>
                <dd>{formatCourseDate(course.createdDateTime)}</dd>
              </div>
              <div>
                <dt>Updated</dt>
                <dd>{formatCourseDate(course.updatedDateTime)}</dd>
              </div>
            </dl>
          </section>

          <div>
            <section className="module-card edu-section" style={{ marginBottom: '1rem' }}>
              <h2>Description</h2>
              {course.description && isRichTextHtml(course.description) ? (
                <div
                  className="edu-rich-text"
                  dangerouslySetInnerHTML={{ __html: course.description }}
                />
              ) : (
                <p style={{ margin: 0, whiteSpace: 'pre-wrap', color: '#374151', fontSize: '0.9rem' }}>
                  {course.description || course.shortDescription || 'No description provided.'}
                </p>
              )}
            </section>

            <section className="module-card edu-section">
              <div className="edu-header-row" style={{ marginBottom: '0.75rem' }}>
                <div>
                  <h2 style={{ margin: 0 }}>
                    <Layers className="w-4 h-4" style={{ display: 'inline', marginRight: 6 }} />
                    Modules
                  </h2>
                  <p className="edu-section__hint" style={{ marginBottom: 0 }}>
                    Organize course content into ordered modules. Lessons come in the next phase.
                  </p>
                </div>
                <button type="button" className="edu-btn edu-btn--primary" onClick={openCreateModule}>
                  <Plus className="w-4 h-4" />
                  Add Module
                </button>
              </div>

              <ModuleAlert message={modulesError} />

              {modulesLoading ? (
                <AppLoader color={THEME} />
              ) : modules.length === 0 ? (
                <div className="edu-empty" style={{ padding: '1.75rem 0.5rem' }}>
                  <Layers className="edu-empty__icon" />
                  <p>No modules have been added yet.</p>
                  <button type="button" className="edu-btn edu-btn--primary" onClick={openCreateModule}>
                    <Plus className="w-4 h-4" />
                    Add Module
                  </button>
                </div>
              ) : (
                <div className="edu-table-wrap">
                  <table className="edu-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Module</th>
                        <th>Description</th>
                        <th>Lessons</th>
                        <th>Status</th>
                        <th className="edu-table__actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modules.map((mod, index) => (
                        <tr key={mod.id}>
                          <td>{mod.displayOrder || index + 1}</td>
                          <td className="edu-title-cell">{mod.title}</td>
                          <td>
                            <span className="edu-module-desc">
                              {mod.description || '—'}
                            </span>
                          </td>
                          <td>{mod.lessonCount}</td>
                          <td>
                            <span
                              className={`edu-status ${mod.isActive ? 'is-active' : 'is-inactive'}`}
                            >
                              {mod.status}
                            </span>
                          </td>
                          <td className="edu-table__actions">
                            <button
                              type="button"
                              className="edu-icon-btn"
                              title="Move up"
                              disabled={actionBusy || index === 0}
                              onClick={() => moveModule(mod, 'up')}
                            >
                              <ArrowUp className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              className="edu-icon-btn"
                              title="Move down"
                              disabled={actionBusy || index === modules.length - 1}
                              onClick={() => moveModule(mod, 'down')}
                            >
                              <ArrowDown className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              className="edu-icon-btn"
                              title="Edit"
                              onClick={() => openEditModule(mod)}
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            {mod.isActive ? (
                              <button
                                type="button"
                                className="edu-icon-btn"
                                title="Inactivate"
                                onClick={() =>
                                  setConfirmAction({ type: 'module-inactivate', target: mod })
                                }
                              >
                                <PowerOff className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="edu-icon-btn is-success"
                                title="Activate"
                                onClick={() =>
                                  setConfirmAction({ type: 'module-activate', target: mod })
                                }
                              >
                                <Power className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              type="button"
                              className="edu-icon-btn is-danger"
                              title="Delete"
                              onClick={() =>
                                setConfirmAction({ type: 'module-delete', target: mod })
                              }
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              className="edu-btn edu-btn--ghost edu-btn--sm"
                              title="Manage lessons"
                              onClick={() =>
                                navigate(
                                  `/education/courses/${course.id}/modules/${mod.id}/lessons`
                                )
                              }
                            >
                              Manage Lessons
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        </div>
      </div>

      {moduleModalOpen ? (
        <div className="edu-modal" role="presentation">
          <button type="button" className="edu-modal__backdrop" onClick={closeModuleModal} aria-label="Close" />
          <div className="edu-modal__panel edu-modal__panel--form" role="dialog" aria-modal="true">
            <div className="edu-modal__head">
              <h2>{editingModule ? 'Edit Module' : 'Add Module'}</h2>
              <button type="button" className="edu-icon-btn" onClick={closeModuleModal} disabled={moduleSaving}>
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={saveModule}>
              <div className="edu-modal__body">
                <div className="edu-field">
                  <label className="edu-label" htmlFor="mod-title">
                    Module Title *
                  </label>
                  <input
                    id="mod-title"
                    className="edu-input"
                    value={moduleForm.title}
                    onChange={(e) => {
                      setModuleForm((p) => ({ ...p, title: e.target.value }));
                      if (moduleFormError) setModuleFormError('');
                    }}
                    disabled={moduleSaving}
                    maxLength={255}
                    required
                    autoFocus
                  />
                </div>
                <div className="edu-field">
                  <label className="edu-label" htmlFor="mod-desc">
                    Description
                  </label>
                  <textarea
                    id="mod-desc"
                    className="edu-textarea"
                    value={moduleForm.description}
                    onChange={(e) => setModuleForm((p) => ({ ...p, description: e.target.value }))}
                    disabled={moduleSaving}
                  />
                </div>
                <div className="edu-field">
                  <label className="edu-label" htmlFor="mod-order">
                    Display Order
                  </label>
                  <input
                    id="mod-order"
                    type="number"
                    min="1"
                    step="1"
                    className="edu-input"
                    value={moduleForm.displayOrder}
                    onChange={(e) => setModuleForm((p) => ({ ...p, displayOrder: e.target.value }))}
                    disabled={moduleSaving}
                  />
                </div>
                <div className="edu-field">
                  <label className="edu-label" htmlFor="mod-status">
                    Status
                  </label>
                  <select
                    id="mod-status"
                    className="edu-select"
                    value={moduleForm.status}
                    onChange={(e) => setModuleForm((p) => ({ ...p, status: e.target.value }))}
                    disabled={moduleSaving}
                  >
                    {COURSE_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                {moduleFormError ? <span className="edu-form-error">{moduleFormError}</span> : null}
              </div>
              <div className="edu-modal__foot">
                <button
                  type="button"
                  className="edu-btn edu-btn--ghost"
                  onClick={closeModuleModal}
                  disabled={moduleSaving}
                >
                  Cancel
                </button>
                <button type="submit" className="edu-btn edu-btn--primary" disabled={moduleSaving}>
                  {moduleSaving ? 'Saving…' : editingModule ? 'Update Module' : 'Create Module'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {confirmAction ? (
        <div className="edu-modal" role="presentation">
          <button
            type="button"
            className="edu-modal__backdrop"
            onClick={() => !actionBusy && setConfirmAction(null)}
            aria-label="Close"
          />
          <div className="edu-modal__panel" role="dialog" aria-modal="true">
            <div className="edu-modal__head">
              <h2>Confirm</h2>
              <button
                type="button"
                className="edu-icon-btn"
                disabled={actionBusy}
                onClick={() => setConfirmAction(null)}
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="edu-modal__body">
              <p>
                {confirmCopy} <strong>{confirmName}</strong>
              </p>
            </div>
            <div className="edu-modal__foot">
              <button
                type="button"
                className="edu-btn edu-btn--ghost"
                disabled={actionBusy}
                onClick={() => setConfirmAction(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`edu-btn ${
                  confirmAction.type === 'delete' || confirmAction.type === 'module-delete'
                    ? 'edu-btn--danger'
                    : 'edu-btn--primary'
                }`}
                disabled={actionBusy}
                onClick={runConfirm}
              >
                {actionBusy ? 'Please wait…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CourseDetails;
